import {ApiUrls} from 'shared/config/model/urls';

import {createApi} from '@reduxjs/toolkit/query/react';

import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {buildQuery} from 'shared/api/query';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';
import {paginationAdapter} from 'shared/api/paginationHelper';
import IPagination from 'shared/api/model/IPagination';
import IHydraResponse from 'shared/api/model/HydraResponse';
import ICollectionParams from 'shared/api/model/ICollectionParams';
import SimpleResponse from 'shared/api/model/SimpleResponse';

import IPromoCode, {EditPromoCodeType} from '../model/IPromoCode';

interface IUpdatePromoCodeRequest extends EditPromoCodeType {
    id: number;
}

interface IActivatePromoCodeRequest {
    value: string;
}

export const promoCodeAPI = createApi({
    reducerPath: 'promoCodeAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['PromoCode'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getPromoCodes: build.query<IPagination<IPromoCode>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.PROMO_CODES(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'PromoCode'),
            transformResponse(
                rawResult: IHydraResponse<IPromoCode>,
            ): Promise<IPagination<IPromoCode>> | IPagination<IPromoCode> {
                return paginationAdapter(rawResult);
            },
        }),
        createPromoCode: build.mutation<IPromoCode, EditPromoCodeType>({
            query: (data) => ({
                path: ApiUrls.PROMO_CODES(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'PromoCode', id: 'LIST' }],
        }),
        updatePromoCode: build.mutation<IPromoCode, IUpdatePromoCodeRequest>({
            query: ({id, ...data}) => ({
                path: ApiUrls.PROMO_CODE(id),
                method: 'PUT',
                data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'PromoCode', id },
                { type: 'PromoCode', id: 'LIST' },
            ],
        }),
        togglePromoCodePublish: build.mutation<IPromoCode, { id: number; published: boolean }>({
            query: ({ id, published }) => ({
                path: ApiUrls.PROMO_CODE(id),
                method: 'PUT',
                data: { published },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'PromoCode', id },
                { type: 'PromoCode', id: 'LIST' },
            ],
        }),
        deletePromoCode: build.mutation<void, number>({
            query: (id) => ({
                path: ApiUrls.PROMO_CODE(id),
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: 'PromoCode', id },
                { type: 'PromoCode', id: 'LIST' },
            ],
        }),
        activatePromoCode: build.mutation<SimpleResponse, IActivatePromoCodeRequest>({
            query: (data) => ({
                path: ApiUrls.PROMO_CODE_ACTIVATE,
                method: 'POST',
                data,
            }),
        }),
    }),
});
